import CookieCard from 'components/CookieCard';
import FormattedText from 'components/FormattedText';
import Select from 'components/Select';
import useTabs from 'hooks/useTabs';
import TextBlock from 'layout/TextBlock';
import React from 'react';
import { Body1, Heading2 } from 'typography';
import styles from './policyTab.module.scss';

export interface PolicyTabProps {
  id: string;
  i18nPrefix: string;
  locali18nPrefix?: string;
  selectOptions: Array<{ label: string; value: string }>;
  content: {
    title: string;
    label: string;
    tabId: string;
    sections?: Array<{
      title: string;
      content: string;
      anchor: string;
    }>;
    intro?: string;
    details?: string;
    listTitle?: string;
    cardLabels?: { [key: string]: string };
  };
  thirdPartyCookies?: Array<{
    title?: string;
    desc?: string;
    link?: string;
  }>;
  categoryCookies?: Array<{
    name: string;
    source: string;
    domain?: string;
    purpose?: string;
    duration?: string;
    additionalInfo?: string;
  }>;
  localContent?: {
    sections?: Array<{
      title: string;
      content: string;
    }>;
  };
  onTabChange?: (tabId: string) => void;
}

const PolicyTab: React.FC<PolicyTabProps> = ({
  i18nPrefix,
  locali18nPrefix,
  content,
  localContent,
  selectOptions,
  thirdPartyCookies,
  categoryCookies,
  onTabChange
}) => {
  const { tabId } = content;
  const { setActiveTab } = useTabs();

  // Use onTabChange if provided, otherwise fall back to setActiveTab
  const handleTabChange = onTabChange || setActiveTab;

  return (
    <div className={styles.root}>
      <>
        <div className={styles.mobile}>
          <Select
            className={styles.policySelect}
            handleChange={handleTabChange}
            options={selectOptions}
            value={content && content.title}
          />
        </div>

        <div className={styles.desktop}>
          <Heading2> {content && content.title} </Heading2>
        </div>

        {content.intro && <FormattedText i18nKey={content.intro} />}

        {content?.sections &&
          content.sections.map((section: any, index: number) => (
            <TextBlock
              key={section.title}
              sectionI18nKey={`${i18nPrefix}.sections.${index}`}
              index={index}
              setActiveTab={setActiveTab}
            />
          ))}

        {localContent?.sections &&
          localContent.sections.map((section: any, index: number) => (
            <TextBlock
              key={section.title}
              sectionI18nKey={`${locali18nPrefix}.sections.${index}`}
              index={index}
              setActiveTab={setActiveTab}
            />
          ))}

        {content.details && <FormattedText i18nKey={content.details} />}

        {thirdPartyCookies && tabId === 'marketing' && (
          <div className={styles.thirdParty}>
            {thirdPartyCookies.map((cookie, index) => {
              const { title } = cookie;
              const { desc } = cookie;
              const { link } = cookie;

              return (
                <div key={`thirdParty-${index}`}>
                  {title && <Body1 bold> {title} </Body1>}
                  {desc && <Body1> {desc} </Body1>}
                  {link && (
                    <Body1>
                      <a href={link}> {link} </a>
                    </Body1>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {content.listTitle && categoryCookies.length > 0 && (
          <FormattedText i18nKey={content.listTitle} />
        )}

        {Array.isArray(categoryCookies) &&
          categoryCookies.length > 0 &&
          categoryCookies?.map((card, index) => (
            <CookieCard key={`card${index}`} info={card} />
          ))}
      </>
    </div>
  );
};

export default PolicyTab;
